import React from "react";
import _ from 'lodash';

import { Box, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup"
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

import PickupLocation from "./PickupLocation";
import { FormProvider } from "../../common/hook-form";
import DropLocation from "./DropLocation";
import PackageDetails from "./PackageDetails";
import OtherDetails from "./OtherDetails";
import { apiConstants, postGeneric } from "../../common/constants";
import { formatDate } from "../../helpers/UtilityHelper";
import PageTitle from "../../common/PageTitle";
import PageHero from "../../common/PageHero";
import { useGetMerchantQuery } from "../../redux/commonRTK";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { FONT, SIZE, WEIGHT } from "../../styles/foundation/typography";
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUCCESS } from "../../styles/foundation/colors";
import { SHADOW } from "../../styles/foundation/effects";
import { pageContainerSx } from "../../styles/patterns";

const stepperSx = {
  '& .MuiStepLabel-label': { fontFamily: FONT, fontSize: SIZE.xs, color: NAVY },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: WEIGHT.bold, color: NAVY },
  '& .MuiStepIcon-root.Mui-active': { color: PRIMARY_BLUE },
  '& .MuiStepIcon-root.Mui-completed': { color: SUCCESS },
};
const stepperCardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: 2.5 };
const formCardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2.5, md: 3.5 } };
const stepTitleSx = { fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: NAVY, mb: 3 };

const steps = [
  "Pickup Details",
  "Delivery Details",
  "Box Details",
  "Other Details",
];

const INVOICE_VALUE_FOR_EWAY_BILL = 50000;

function CreateMPSShipment() {

  const [activeStep, setActiveStep] = React.useState(0);
  const navigate = useNavigate();
  const [creatingOrder, setIsCreatingOrder] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const merchant = getMerchant();

  const { data } = useGetMerchantQuery({ id: merchant.id });
  const pickupLocations = data?.pickup_details || [];

  const methods = useForm({
    defaultValues: {
      // invoice_date: new Date(),
      riskType: 'OWNER',
      payment_mode: 'PREPAID',
      insured: false,
      appointment_delivery: false,
      invoice_value: 0,
      reference: '',
      invoice_number: '',
      ewaybill: '',
      ewb_exp_date: null,
      invoice_date: null,
    },
    resolver: yupResolver(
      Yup.object().shape({
        pickupLocation: Yup.string().required("Please enter the pickup location."),
        delivery_location: Yup.string().required("Delivery Location is Required!"),
        delivery_details: Yup.object().shape({
          to_name: Yup.string().required("Customer Name is Required!"),
          to_phone_number: Yup.string(),
          to_address: Yup.string().required("Customer Address is Required!"),
          to_email: Yup.string(),
          to_pincode: Yup.string().required("Address Pincode is Required!"),
          to_city: Yup.string().required("Address City is Required!"),
          to_state: Yup.string().required("Address State is Required!"),
          gstin: Yup.string(),
        }),
        item_list: Yup.array(Yup.object().shape({
          item_name: Yup.string().required(""),
          units: Yup.number().required("").min(1, ""),
          additional: Yup.object().shape({
            length: Yup.number().required("").min(1, ""),
            breadth: Yup.number().required("").min(1, ""),
            height: Yup.number().required("").min(1, ""),
            weight: Yup.number().required("").min(1, "")
          })
        })),
        invoice_value: Yup.number().required("").min(1, ""),
        selling_price: Yup.number().required("").min(1, ""),
        invoice_date: Yup.date().typeError("Please select a valid invoice date").required("Invoice date is a mandatory field!"),
        payment_mode: Yup.string().required(""),
        cod_amount: Yup.number().when('payment_mode', {
          is: val => val === 'COD',
          then: Yup.number().required(""),
        }).min(1, ""),
        reference: Yup.string().required(""),
        insured: Yup.boolean().required(""),
        appointment_delivery: Yup.boolean().required(""),
        invoice_number: Yup.string().required(""),
        ewaybill: Yup.string().when("invoice_value", {
          is: val => val > INVOICE_VALUE_FOR_EWAY_BILL,
          then: Yup.string().required("Eway bill number is required for invoices > Rs. 50,000")
        }),
        courier_id: Yup.string().when(["invoice_value", "ewaybill"], {
          is: (invoice_value, ewaybill) => invoice_value > INVOICE_VALUE_FOR_EWAY_BILL && ewaybill,
          then: Yup.string().required("Courier Id is mandatory")
        }),
        // ewb_exp_date: Yup.date().optional(),
        riskType: Yup.string().required(""),
      })
    ),
  });

  const { handleSubmit } = methods;


  const onSubmit = (values) => {

    setIsCreatingOrder(true)

    const formData = JSON.parse(JSON.stringify(values));

    // console.log(formData);
    const request = {
      ...formData,
      order_type: 'Forward Shipment',
      skipRule: false,
      is_reverse: false,
      merchantOId: merchant.id
    }

    const selectedAddress = pickupLocations.find(loc => loc.id === formData.pickupLocation);
    request.pickup_details = {
      from_address: selectedAddress.address_line,
      from_city: selectedAddress.city,
      from_country: selectedAddress.country,
      from_name: selectedAddress.name,
      from_pincode: selectedAddress.pincode,
      from_state: selectedAddress.state,
      from_phone_number: selectedAddress.contactNumber,
      from_email: selectedAddress.email,
      gstin: selectedAddress.gstin
    }

    if (request.payment_mode === 'PREPAID') {
      request.cod_amount = 0;
    }

    if (request.invoice_date) {
      request.invoice_date = formatDate(request.invoice_date, "yyyy-MM-dd hh:mm:ss")
    }

    if (request.ewb_exp_date) {
      request.ewb_exp_date = formatDate(request.ewb_exp_date, "yyyy-MM-dd hh:mm:ss")
    }

    request.item_list.forEach((item) => {
      let deadWeight = item.additional.weight;
      let volWeight = (item.additional.length * item.additional.breadth * item.additional.height) / 4500;
      item.additional.weight = Math.max(deadWeight, volWeight) * 1000;

      const mps_gst_info = {
        /* enterprise_gstin: "demogst",
        taxable_value: 12.01,
        place_of_supply: "place_of_supply",
        hsn_code: "hsnCode",
        gst_total_tax: 12.09,
        consignee_gstin: "consignee_gstin", */

        seller_gstin: request.pickup_details.gstin,
        ewaybill_serial_number: request.ewaybill,
        is_seller_registered_under_gst: request.pickup_details.gstin ? true : false,
        invoice_reference: request.reference
      }
      item.mps_gst_info = mps_gst_info;
    })

    request.shipment_detail = [{
      item_weight: _.sumBy(request.item_list, 'additional.weight'),
      item_length: _.sumBy(request.item_list, 'additional.length'),
      item_breadth: _.sumBy(request.item_list, 'additional.breadth'),
      item_height: _.sumBy(request.item_list, 'additional.height')
    }]

    if (request.courier_id) {
      request.skipRule = true;
      request.courier_id = {
        prozoId: request.courier_id
      }
    }

    // return false;

    postGeneric(apiConstants.ASYNC_B2B_CREATE_ORDER, request)
      .then(() => {
        enqueueSnackbar("Successfully Created!", { variant: 'success' });
        navigate("/b2b-orders");
      }).catch((err) => {
        const message = err?.response?.data?.message || err.message;
        enqueueSnackbar(message || "Error creating order", { variant: "error" });
      }).finally(() => {
        setIsCreatingOrder(false)
      })
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle>Create MPS Shipment</PageTitle>
      {/* <PageHero title='Create MPS Shipment' subtitle='Book a multi-piece B2B shipment' sx={{ mb: 2.5 }} /> */}
      <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12} md={3}>
          <Box sx={stepperCardSx}>
            <Stepper orientation="vertical" activeStep={activeStep} sx={stepperSx}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box sx={formCardSx}>
            <Typography sx={stepTitleSx}>{activeStep + 1}. {steps[activeStep]}</Typography>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              {activeStep === 0 && (
                <PickupLocation useIdAsKey setActiveStep={setActiveStep} />
              )}
              {activeStep === 1 && (
                <DropLocation setActiveStep={setActiveStep} />
              )}
              {activeStep === 2 && (
                <PackageDetails setActiveStep={setActiveStep} />
              )}
              {activeStep === 3 && (
                <OtherDetails creatingOrder={false} setActiveStep={setActiveStep} />
              )}
            </FormProvider>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default CreateMPSShipment